import { z } from 'zod';

export const appointmentAutomationChannelSchema = z.enum(['whatsapp', 'sms', 'email', 'voice']);
export const appointmentAutomationTriggerSchema = z.enum(['created', 'status_changed', 'rescheduled', 'reminder']);
export const appointmentAutomationStatusSchema = z.enum(['scheduled', 'confirmed', 'reminded', 'rescheduled', 'cancelled', 'completed', 'no_show']);
export const appointmentAutomationLanguageSchema = z.enum(['it', 'en']);
export const appointmentAutomationReminderHoursSchema = z.number().finite().nonnegative().nullable();
const nullableText = z.string().nullable();
const attachmentSchema = z.object({ type: z.string() }).catchall(z.unknown());
const fields = {
  name: z.string().trim().min(1),
  trigger_event: appointmentAutomationTriggerSchema,
  channel: appointmentAutomationChannelSchema,
  trigger_status: appointmentAutomationStatusSchema.nullable().optional(),
  service_type_id: nullableText.optional(),
  template_id: nullableText.optional(),
  template_name: nullableText.optional(),
  message_body: nullableText.optional(),
  email_subject: nullableText.optional(),
  sender_id: nullableText.optional(),
  sender_display: nullableText.optional(),
  delay_minutes: z.number().finite().int().nonnegative().optional(),
  reminder_hours_before: appointmentAutomationReminderHoursSchema.optional(),
  is_active: z.boolean().optional(),
  priority: z.number().finite().int().optional(),
  conditions: z.record(z.unknown()).optional(),
  attachments: z.array(attachmentSchema).optional(),
  template_kind: nullableText.optional(),
  is_system_default: z.boolean().optional(),
  whatsapp_template_id: nullableText.optional(),
  system_key: nullableText.optional(),
  voice_script: nullableText.optional(),
  voice_language: appointmentAutomationLanguageSchema.nullable().optional(),
  voice_provider: z.enum(['openai', 'elevenlabs', 'gemini']).nullable().optional(),
  voice_max_attempts: z.number().int().positive().nullable().optional(),
  voice_press_to_confirm: z.boolean().nullable().optional(),
  voice_template_id: nullableText.optional(),
  voice_agent_id: nullableText.optional(),
  voice_trunk_id: nullableText.optional(),
};

export const appointmentAutomationInputSchema = z.object({
  ...fields,
  // Legacy offered Gmail choice is an email transport selected by sender_id.
  channel: z.enum(['whatsapp', 'sms', 'email', 'voice', 'gmail'])
    .transform(value => value === 'gmail' ? 'email' : value),
}).strict();
export const appointmentAutomationPatchSchema = appointmentAutomationInputSchema.partial();
export type AppointmentAutomationInput = z.infer<typeof appointmentAutomationInputSchema>;
export type AppointmentAutomationPatch = z.infer<typeof appointmentAutomationPatchSchema>;

// DB dates/numeric are deliberately not accepted by the wire contract.
export const appointmentAutomationSchema = z.object(fields).extend({
  id: z.string(), tenant_id: z.string(),
  trigger_status: appointmentAutomationStatusSchema.nullable(),
  service_type_id: nullableText, template_id: nullableText, template_name: nullableText,
  message_body: nullableText, delay_minutes: z.number().int().nonnegative(),
  reminder_hours_before: appointmentAutomationReminderHoursSchema,
  is_active: z.boolean(), priority: z.number().int(),
  conditions: z.record(z.unknown()),
  executions_count: z.number().int().nonnegative(),
  last_executed_at: z.string().datetime().nullable(),
  created_at: z.string().datetime(), updated_at: z.string().datetime(),
  service_type_name: nullableText.optional(),
});
export type AppointmentAutomation = z.infer<typeof appointmentAutomationSchema>;
const dbReminderHours = z.union([z.number(), z.string().min(1)])
  .transform(Number).pipe(z.number().finite().nonnegative()).nullable();
export const appointmentAutomationRowSchema = appointmentAutomationSchema.extend({
  reminder_hours_before: dbReminderHours,
  last_executed_at: z.date().nullable(),
  created_at: z.date(), updated_at: z.date(),
});
export type AppointmentAutomationRow = z.infer<typeof appointmentAutomationRowSchema>;
export function toAppointmentAutomation(input: unknown): AppointmentAutomation {
  const row = appointmentAutomationRowSchema.parse(input);
  return appointmentAutomationSchema.parse({
    ...row,
    last_executed_at: row.last_executed_at?.toISOString() ?? null,
    created_at: row.created_at.toISOString(),
    updated_at: row.updated_at.toISOString(),
  });
}

// Form values are not DB rows or write payloads: select blanks and UI toggles
// belong only here. All numeric fields remain numbers, including nullable hours.
export const appointmentAutomationFormSchema = z.object({
  name: z.string(), trigger_event: z.string(), trigger_status: z.string(),
  service_type_id: z.string(), channel: z.string(),
  template_id: z.string(), template_name: z.string(), message_body: z.string(),
  email_subject: z.string(), use_template: z.boolean(),
  delay_minutes: z.number(), reminder_hours_before: appointmentAutomationReminderHoursSchema,
  is_active: z.boolean(), priority: z.number(), sender_id: z.string(), sender_display: z.string(),
  attach_ics: z.boolean(), whatsapp_template_id: z.string(), recipient: z.string(),
  voice_script: z.string(), voice_language: z.string(), voice_provider: z.string(),
  voice_max_attempts: z.number(), voice_press_to_confirm: z.boolean(),
  voice_agent_id: z.string(), voice_trunk_id: z.string(),
});
export type AppointmentAutomationForm = z.infer<typeof appointmentAutomationFormSchema>;

export const appointmentAutomationPreviewInputSchema = z.object({
  body: z.string().optional(), subject: nullableText.optional(),
  appointment_id: z.string().min(1).nullable().optional(),
  language: appointmentAutomationLanguageSchema.nullable().optional(),
  template_id: nullableText.optional(),
  channel: appointmentAutomationChannelSchema.nullable().optional(),
  use_fictitious: z.boolean().optional(),
}).strict().transform(value => ({
  ...value, use_fictitious: value.appointment_id ? false : true,
}));
export const appointmentAutomationPreviewResultSchema = z.object({
  channel: appointmentAutomationChannelSchema,
  subject: nullableText, body: z.string(), body_html: nullableText,
  body_format: z.enum(['html', 'text']),
  template_name: nullableText, template_id: nullableText,
  sender_display: nullableText, recipient: z.enum(['customer', 'consultant']),
  recipient_address: nullableText, has_ics_attachment: z.boolean(),
  length: z.number().int().nonnegative(),
});
export type AppointmentAutomationPreviewResult = z.infer<typeof appointmentAutomationPreviewResultSchema>;
export const appointmentAutomationPromptResultSchema = z.object({
  prompt: z.string(), length: z.number(), hash: z.string(),
  trigger: z.string(), language: appointmentAutomationLanguageSchema,
  context: z.record(z.unknown()).optional(),
});
export type AppointmentAutomationPromptResult = z.infer<typeof appointmentAutomationPromptResultSchema>;
export const appointmentAutomationDraftPreviewResultSchema = z.object({
  subject: nullableText, body: z.string(), unresolved: z.array(z.string()),
  variables: z.record(z.string()),
  appointment: z.object({
    id: z.string(), title: nullableText, start_time: nullableText, customer_name: nullableText,
  }),
  is_fictitious: z.boolean(),
});
export type AppointmentAutomationDraftPreviewResult = z.infer<typeof appointmentAutomationDraftPreviewResultSchema>;
export const appointmentAutomationTestInputSchema = z.object({
  test_phone: z.string().trim().min(1).optional(),
  test_email: z.string().trim().email().optional(),
  // Test sends support synthetic context only. Real appointment selection is
  // available on preview, not on this endpoint.
  use_fictitious: z.literal(true).default(true),
}).strict().refine(value => value.test_phone || value.test_email, 'Specificare test_phone o test_email');

export const automationDispatchResultSchema = z.discriminatedUnion('status', [
  z.object({ status: z.literal('queued'), referenceId: z.string() }),
  z.object({ status: z.literal('accepted'), providerMessageId: nullableText }),
  z.object({ status: z.literal('delivered'), providerMessageId: nullableText }),
  z.object({ status: z.literal('skipped'), reason: z.string() }),
  z.object({ status: z.literal('retryable_failure'), reason: z.string() }),
  z.object({ status: z.literal('permanent_failure'), reason: z.string() }),
  z.object({ status: z.literal('uncertain'), reason: z.string(), providerMessageId: nullableText.optional() }),
]);
export type AutomationDispatchResult = z.infer<typeof automationDispatchResultSchema>;
// Correlates a signed provider receipt with one persisted attempt, including
// receipts that arrive before the send request returns its provider ID.
export const calendarDeliveryReceiptSchema = z.object({
  deliveryId: z.string().uuid(),
  claimToken: z.string().uuid(),
});
export type CalendarDeliveryReceipt = z.infer<typeof calendarDeliveryReceiptSchema>;
export const appointmentAutomationTestResultSchema = z.object({
  message_sid: nullableText,
  provider_message_id: nullableText.optional(),
  channel: appointmentAutomationChannelSchema,
  recipient: z.string(),
  dispatch: automationDispatchResultSchema.optional(),
});
export type AppointmentAutomationTestResult = z.infer<typeof appointmentAutomationTestResultSchema>;
export const appointmentAutomationTestStatusSchema = z.object({
  message_sid: z.string().optional(), date_sent: nullableText.optional(),
  status: z.string(), error_code: nullableText.optional(), error_message: nullableText.optional(),
});
export const appointmentAutomationTestSendSchema = z.object({
  id: z.string(), channel: z.string(), recipient: z.string(), status: z.string(),
  provider_message_id: nullableText, error: nullableText,
  sent_by_user_id: nullableText, sent_by_user_name: nullableText,
  created_at: z.string(), delivery_status: nullableText,
  delivery_status_updated_at: nullableText, delivery_error_code: nullableText,
  delivery_error_message: nullableText,
});
export type AppointmentAutomationTestSend = z.infer<typeof appointmentAutomationTestSendSchema>;
export const appointmentAutomationPreviewAppointmentSchema = z.object({
  id: z.string(), customer_name: nullableText.optional(),
  start_time: nullableText.optional(), service_type_name: nullableText.optional(),
  status: nullableText.optional(),
});
export type AppointmentAutomationPreviewAppointment = z.infer<typeof appointmentAutomationPreviewAppointmentSchema>;
export const appointmentAutomationVoiceStatsSchema = z.object({
  total: z.number(), answered: z.number(), no_answer: z.number(), failed: z.number(),
  confirmed_voice: z.number(), cancelled_voice: z.number(), rescheduled_voice: z.number(),
});
export type AppointmentAutomationVoiceStats = z.infer<typeof appointmentAutomationVoiceStatsSchema>;

// Template picker projections used by the automation editor. These model the
// existing messaging/email responses, not automation DB rows or write payloads.
export const appointmentAutomationWhatsAppTemplateOptionSchema = z.object({
  id: z.string(), name: z.string(), content_sid: z.string(),
  body_text: z.string(), category: z.string(), friendly_name: z.string().optional(),
});
export type AppointmentAutomationWhatsAppTemplateOption = z.infer<typeof appointmentAutomationWhatsAppTemplateOptionSchema>;
export const appointmentAutomationEmailTemplateOptionSchema = z.object({
  id: z.string(), name: z.string(), subject: z.string().nullable(),
  is_default: z.boolean().nullable(),
});
export type AppointmentAutomationEmailTemplateOption = z.infer<typeof appointmentAutomationEmailTemplateOptionSchema>;

export const calendarNotificationEventSchema = z.enum([
  'appointment_created', 'appointment_updated', 'appointment_cancelled',
  'public_form_submission', 'hr_availability_conflict',
]);
export const calendarNotificationChannelSchema = z.enum(['toast', 'badge', 'push']);
export const calendarNotificationRecipientsSchema = z.enum(['admins', 'assigned', 'all']);
export const calendarNotificationEventConfigSchema = z.object({
  enabled: z.boolean(),
  channels: z.object({ toast: z.boolean(), badge: z.boolean(), push: z.boolean() }).strict(),
  recipients: calendarNotificationRecipientsSchema,
}).strict();
const eventShape = {
  appointment_created: calendarNotificationEventConfigSchema,
  appointment_updated: calendarNotificationEventConfigSchema,
  appointment_cancelled: calendarNotificationEventConfigSchema,
  public_form_submission: calendarNotificationEventConfigSchema,
  hr_availability_conflict: calendarNotificationEventConfigSchema,
};
export const calendarNotificationSettingsSchema = z.object({
  events: z.object(eventShape).strict(),
}).strict();
const eventPatch = calendarNotificationEventConfigSchema.extend({
  channels: calendarNotificationEventConfigSchema.shape.channels.partial(),
}).partial();
export const calendarNotificationSettingsPatchSchema = z.object({
  events: z.object({
    appointment_created: eventPatch.optional(), appointment_updated: eventPatch.optional(),
    appointment_cancelled: eventPatch.optional(), public_form_submission: eventPatch.optional(),
    hr_availability_conflict: eventPatch.optional(),
  }).strict().optional(),
}).strict();
export const calendarNotificationSettingsUpdateSchema = z.object({
  settings: calendarNotificationSettingsPatchSchema,
}).strict();
export const calendarNotificationSettingsResponseSchema = z.object({
  settings: calendarNotificationSettingsSchema,
  event_labels: z.record(calendarNotificationEventSchema, z.string()).optional(),
});
export type CalendarNotificationEvent = z.infer<typeof calendarNotificationEventSchema>;
export type CalendarNotificationChannel = z.infer<typeof calendarNotificationChannelSchema>;
export type CalendarNotificationRecipients = z.infer<typeof calendarNotificationRecipientsSchema>;
export type CalendarNotificationEventConfig = z.infer<typeof calendarNotificationEventConfigSchema>;
export type CalendarNotificationSettings = z.infer<typeof calendarNotificationSettingsSchema>;
export type CalendarNotificationSettingsPatch = z.infer<typeof calendarNotificationSettingsPatchSchema>;
export type CalendarNotificationSettingsResponse = z.infer<typeof calendarNotificationSettingsResponseSchema>;
export function mergeCalendarNotificationSettings(
  base: CalendarNotificationSettings,
  patch: CalendarNotificationSettingsPatch,
): CalendarNotificationSettings {
  const result = calendarNotificationSettingsSchema.parse(base);
  const parsed = calendarNotificationSettingsPatchSchema.parse(patch);
  for (const key of calendarNotificationEventSchema.options) {
    const change = parsed.events?.[key];
    if (!change) continue;
    const current = result.events[key];
    result.events[key] = {
      enabled: change.enabled ?? current.enabled,
      recipients: change.recipients ?? current.recipients,
      channels: {
        toast: change.channels?.toast ?? current.channels.toast,
        badge: change.channels?.badge ?? current.channels.badge,
        push: change.channels?.push ?? current.channels.push,
      },
    };
  }
  return result;
}
